/**
 * Bot AI logic - finds shortest path to Philosopher's Stone
 */

import { RECIPES, findRecipe } from './recipes';
import { ELEMENTS } from './elements';
import { Card } from '../types/game';

interface PathNode {
  element: string;
  distance: number;
  recipe?: [string, string]; // What to combine to get this element
}

/**
 * Build a map of shortest distances from any element to philosopher_stone
 * Uses BFS from philosopher_stone backwards
 */
function buildDistanceMap(): Map<string, PathNode> {
  const distances = new Map<string, PathNode>();
  const queue: string[] = ['philosopher_stone'];

  distances.set('philosopher_stone', { element: 'philosopher_stone', distance: 0 });

  while (queue.length > 0) {
    const current = queue.shift()!;
    const currentDist = distances.get(current)!.distance;

    // Find all recipes that produce current element
    for (const [inputs, output] of RECIPES) {
      if (output === current) {
        const [input1, input2] = inputs;

        // Update distances for inputs if we found a shorter path
        for (const input of [input1, input2]) {
          const newDist = currentDist + 1;

          if (!distances.has(input) || distances.get(input)!.distance > newDist) {
            distances.set(input, {
              element: input,
              distance: newDist,
              recipe: inputs as [string, string]
            });
            queue.push(input);
          }
        }
      }
    }
  }

  return distances;
}

// Pre-calculate distance map
const DISTANCE_MAP = buildDistanceMap();

/**
 * Get distance from element to philosopher_stone
 * Returns Infinity if unreachable
 */
export function getDistanceToGoal(elementKey: string): number {
  return DISTANCE_MAP.get(elementKey)?.distance ?? Infinity;
}

/**
 * Evaluate how good a move is
 * Returns the improvement in distance to goal
 */
function evaluateMove(card1: Card, card2: Card, hand: Card[]): number {
  const result = findRecipe(card1.element, card2.element);
  if (!result) return -Infinity; // Invalid move

  // COMPLETELY IGNORE moves that create duplicates
  const duplicateCount = hand.filter(c => c.element === result).length;
  if (duplicateCount > 0) {
    return -Infinity; // Never create duplicates
  }

  const currentBest = Math.min(
    getDistanceToGoal(card1.element),
    getDistanceToGoal(card2.element)
  );

  const newDistance = getDistanceToGoal(result);

  // Improvement is negative distance change (lower is better)
  // Add small bonus for higher level elements
  const improvement = currentBest - newDistance;
  const levelBonus = (ELEMENTS[result]?.l ?? 0) * 0.1;

  return improvement + levelBonus;
}

/**
 * Bot AI: Choose best move from hand and table
 * Returns [cardId1, cardId2] or null if no valid moves
 */
export function chooseBotMove(hand: Card[], table: Card[]): [string, string] | null {
  let bestMove: [string, string] | null = null;
  let bestScore = -Infinity;

  // Try all hand + hand pairs (always allowed)
  for (let i = 0; i < hand.length; i++) {
    for (let j = i + 1; j < hand.length; j++) {
      const card1 = hand[i];
      const card2 = hand[j];

      const score = evaluateMove(card1, card2, hand);

      if (score > bestScore) {
        bestScore = score;
        bestMove = [card1.id, card2.id];
      }
    }
  }

  // Try all hand + table pairs (always allowed)
  for (let i = 0; i < hand.length; i++) {
    for (let j = 0; j < table.length; j++) {
      const card1 = hand[i];
      const card2 = table[j];

      const score = evaluateMove(card1, card2, hand);

      if (score > bestScore) {
        bestScore = score;
        bestMove = [card1.id, card2.id];
      }
    }
  }

  // Try all table + table pairs (only if hand < 6 cards)
  if (hand.length < 6) {
    for (let i = 0; i < table.length; i++) {
      for (let j = i + 1; j < table.length; j++) {
        const card1 = table[i];
        const card2 = table[j];

        const score = evaluateMove(card1, card2, hand);

        if (score > bestScore) {
          bestScore = score;
          bestMove = [card1.id, card2.id];
        }
      }
    }
  }

  // If no good move found, return null (bot will discard or end turn)
  return bestScore > -Infinity ? bestMove : null;
}

/**
 * Check if card is useful (can be combined with something)
 */
function isCardUseful(card: Card, hand: Card[], table: Card[]): boolean {
  const allCards = [...hand, ...table];

  for (const other of allCards) {
    if (other.id === card.id) continue;
    const result = findRecipe(card.element, other.element);
    if (result) return true;
  }

  return false;
}

/**
 * Evaluate card value for discard decision
 * Lower score = more likely to discard
 */
function evaluateCardForDiscard(card: Card, hand: Card[], table: Card[]): number {
  let score = 0;

  // Base score: inverse of distance (closer to goal = higher score)
  const distance = getDistanceToGoal(card.element);
  if (distance === Infinity) {
    score -= 100; // Unreachable elements are worst
  } else {
    score += 20 - distance; // Closer = better
  }

  // Penalty for duplicates (depends on element level)
  const duplicateCount = hand.filter(c => c.element === card.element).length;
  const level = ELEMENTS[card.element]?.l ?? 1;

  if (level > 3) {
    // High-level elements: penalty for 3+ copies
    if (duplicateCount >= 3) {
      score -= 12; // Strong penalty for 3rd+ duplicate of high-level element
    } else if (duplicateCount === 2) {
      score -= 4; // Small penalty for 2nd duplicate
    }
  } else {
    // Low-level elements: penalty for 4+ copies
    if (duplicateCount >= 4) {
      score -= 10; // Penalty for 4th+ duplicate
    } else if (duplicateCount === 3) {
      score -= 3; // Small penalty for 3rd duplicate
    }
  }

  // Check if card is useful (can be combined)
  if (!isCardUseful(card, hand, table)) {
    score -= 10; // Moderate penalty for currently unusable cards
  }

  // Bonus for level 5+ elements (legendary)
  if (level >= 5) {
    score += 10; // Keep legendary elements
  }

  // Penalty for cards that lead to dead ends
  // Example: if we have gold, sun is less useful (sun only makes gold)
  const hasGold = hand.some(c => c.element === 'gold');
  if (hasGold && (card.element === 'sun' || card.element === 'glass')) {
    score -= 8; // Sun and glass only make gold
  }

  const hasMagic = hand.some(c => c.element === 'magic');
  if (hasMagic && (card.element === 'moon' || card.element === 'sword' || card.element === 'storm')) {
    score -= 8; // These only make magic
  }

  const hasLife = hand.some(c => c.element === 'life');
  if (hasLife && (card.element === 'tree' || card.element === 'ice')) {
    score -= 8; // These only make life
  }

  return score;
}

/**
 * Bot AI: Choose cards to discard intelligently
 * Discards duplicates, useless cards, and dead-end cards
 */
export function chooseBotDiscard(hand: Card[], table: Card[], count: number): string[] {
  if (count <= 0 || hand.length === 0) return [];

  // Evaluate each card
  const evaluated = hand.map(card => ({
    card,
    score: evaluateCardForDiscard(card, hand, table)
  }));

  // Sort by score (lowest first = worst cards)
  evaluated.sort((a, b) => a.score - b.score);

  // Return worst cards
  return evaluated.slice(0, Math.min(count, evaluated.length)).map(e => e.card.id);
}

/**
 * Identify cards that should be discarded proactively
 * Only triggers when hand is very full (>7 cards)
 */
export function getCardsToDiscard(hand: Card[], table: Card[]): string[] {
  // Only discard proactively if hand is very full
  if (hand.length <= 7) return [];

  const toDiscard: string[] = [];

  // Find duplicates
  const elementCounts = new Map<string, Card[]>();
  for (const card of hand) {
    if (!elementCounts.has(card.element)) {
      elementCounts.set(card.element, []);
    }
    elementCounts.get(card.element)!.push(card);
  }

  // Discard duplicates based on element level
  for (const cards of elementCounts.values()) {
    if (cards.length < 2) continue; // No duplicates

    const element = cards[0].element;
    const level = ELEMENTS[element]?.l ?? 1;

    // For high-level elements (>3): keep max 2 copies
    // For low-level elements (<=3): keep max 3 copies
    const maxCopies = level > 3 ? 2 : 3;

    if (cards.length > maxCopies) {
      // Sort by quality and keep only the best
      const sorted = cards.sort((a, b) =>
        evaluateCardForDiscard(b, hand, table) - evaluateCardForDiscard(a, hand, table)
      );
      toDiscard.push(...sorted.slice(maxCopies).map(c => c.id));
    }
  }

  // Limit to 4 cards max (game rule)
  return toDiscard.slice(0, 4);
}

/**
 * Choose cards from table to discard when no moves available
 * Discards high-level cards (max level or max-1)
 */
export function chooseTableCardsToDiscard(table: Card[]): string[] {
  if (table.length === 0) return [];

  // Find max level on table
  const maxLevel = Math.max(...table.map(c => ELEMENTS[c.element]?.l ?? 1));

  // Get cards of max level or max-1
  const highLevelCards = table.filter(c => {
    const level = ELEMENTS[c.element]?.l ?? 1;
    return level >= maxLevel - 1;
  });

  // Sort by level (highest first), then by distance to goal (furthest first)
  highLevelCards.sort((a, b) => {
    const levelA = ELEMENTS[a.element]?.l ?? 1;
    const levelB = ELEMENTS[b.element]?.l ?? 1;
    if (levelA !== levelB) return levelB - levelA; // Higher level first

    const distA = getDistanceToGoal(a.element);
    const distB = getDistanceToGoal(b.element);
    return distB - distA; // Further from goal first
  });

  // Return up to 4 cards
  return highLevelCards.slice(0, Math.min(4, highLevelCards.length)).map(c => c.id);
}

/**
 * Choose cards from hand to discard on first turn when no moves available
 * Discards all cards of minimum level
 */
export function chooseMinLevelCardsFromHand(hand: Card[]): string[] {
  if (hand.length === 0) return [];

  // Find min level in hand
  const minLevel = Math.min(...hand.map(c => ELEMENTS[c.element]?.l ?? 1));

  // Get all cards of min level
  const minLevelCards = hand.filter(c => {
    const level = ELEMENTS[c.element]?.l ?? 1;
    return level === minLevel;
  });

  // Return up to 4 cards
  return minLevelCards.slice(0, Math.min(4, minLevelCards.length)).map(c => c.id);
}

/**
 * Bot decision: Should bot discard or combine?
 * Returns 'combine' or 'discard' or 'end_turn'
 */
export function botDecideAction(hand: Card[], table: Card[]): 'combine' | 'discard' | 'end_turn' {
  // Always try to combine if possible
  const move = chooseBotMove(hand, table);
  if (move) return 'combine';

  // If hand is full, discard from hand
  if (hand.length > 7) {
    console.log(`🤖 Рука переполнена (${hand.length} карт), нужно сбросить`);
    return 'discard';
  }

  // If no moves and table has cards, discard high-level cards from table
  if (table.length > 0) {
    console.log(`🤖 Нет ходов, сброшу карты со стола`);
    return 'discard';
  }

  // Otherwise end turn
  return 'end_turn';
}
